
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "apache-atlas-hook-for-apache-storm"
    }}>{`Apache Atlas Hook for Apache Storm`}</h1>
    <h2 {...{
      "id": "introduction"
    }}>{`Introduction`}</h2>
    <p>{`Apache Storm is a distributed real-time computation system. Storm makes it
easy to reliably process unbounded streams of data, doing for real-time
processing what Hadoop did for batch processing. The process is essentially
a DAG of nodes, which is called `}<em parentName="p">{`topology`}</em>{`.`}</p>
    <p>{`Apache Atlas is a metadata repository that enables end-to-end data lineage,
search and associate business classification.`}</p>
    <p>{`The goal of this integration is to push the operational topology
metadata along with the underlying data source(s), target(s), derivation
processes and any available business context so Atlas can capture the
lineage for this topology.`}</p>
    <p>{`There are 2 parts in this process detailed below:`}</p>
    <ul>
      <li parentName="ul">{`Data model to represent the concepts in Storm`}</li>
      <li parentName="ul">{`Storm Atlas Hook to update metadata in Atlas`}</li>
    </ul>
    <h2 {...{
      "id": "storm-data-model"
    }}>{`Storm Data Model`}</h2>
    <p>{`A data model is represented as Types in Atlas. It contains the descriptions
of various nodes in the topology graph, such as spouts and bolts and the
corresponding producer and consumer types.`}</p>
    <p>{`The following types are added in Atlas.`}</p>
    <ul>
      <li parentName="ul">{`storm_topology - represents the coarse-grained topology. A storm_topology derives from an Atlas Process type and hence can be used to inform Atlas about lineage.`}</li>
      <li parentName="ul">{`Following data sets are added - kafka_topic, jms_topic, hbase_table, hdfs_data_set. These all derive from an Atlas Dataset type and hence form the end points of a lineage graph.`}</li>
      <li parentName="ul">{`storm_spout - Data Producer having outputs, typically Kafka, JMS`}</li>
      <li parentName="ul">{`storm_bolt - Data Consumer having inputs and outputs, typically Hive, HBase, HDFS, etc.`}</li>
    </ul>
    <p>{`The Storm Atlas hook auto registers dependent models like the Hive data model
if it finds that these are not known to the Atlas server.`}</p>
    <p>{`The data model for each of the types is described in
the class definition at org.apache.atlas.storm.model.StormDataModel.`}</p>
    <h2 {...{
      "id": "storm-atlas-hook"
    }}>{`Storm Atlas Hook`}</h2>
    <p>{`Atlas is notified when a new topology is registered successfully in
Storm. Storm provides a hook, backtype.storm.ISubmitterHook, at the Storm client used to
submit a storm topology.`}</p>
    <p>{`The Storm Atlas hook intercepts the hook post execution and extracts the metadata from the
topology and updates Atlas using the types defined. Atlas implements the
Storm client hook interface in org.apache.atlas.storm.hook.StormAtlasHook.`}</p>
    <h2 {...{
      "id": "limitations"
    }}>{`Limitations`}</h2>
    <p>{`The following apply for the first version of the integration.`}</p>
    <ul>
      <li parentName="ul">{`Only new topology submissions are registered with Atlas, any lifecycle changes are not reflected in Atlas.`}</li>
      <li parentName="ul">{`The Atlas server needs to be online when a Storm topology is submitted for the metadata to be captured.`}</li>
      <li parentName="ul">{`The Hook currently does not support capturing lineage for custom spouts and bolts.`}</li>
    </ul>
    <h2 {...{
      "id": "installation"
    }}>{`Installation`}</h2>
    <p>{`The Storm Atlas Hook needs to be manually installed in Storm on the client side.`}</p>
    <ul>
      <li parentName="ul">{`untar apache-atlas-\${project.version}-storm-hook.tar.gz`}</li>
      <li parentName="ul">{`cd apache-atlas-storm-hook-\${project.version}`}</li>
      <li parentName="ul">{`Copy entire contents of folder apache-atlas-storm-hook-\${project.version}/hook/storm to $ATLAS_PACKAGE/hook/storm`}</li>
    </ul>
    <p>{`Storm Atlas hook jars in $ATLAS_PACKAGE/hook/storm need to be copied to $STORM_HOME/extlib.
Replace STORM_HOME with storm installation path.`}</p>
    <p>{`Restart all daemons after you have installed the atlas hook into Storm.`}</p>
    <h2 {...{
      "id": "configuration"
    }}>{`Configuration`}</h2>
    <h3 {...{
      "id": "storm-configuration"
    }}>{`Storm Configuration`}</h3>
    <p>{`The Storm Atlas Hook needs to be configured in Storm client config
in `}<em parentName="p">{`$STORM_HOME/conf/storm.yaml`}</em>{` as:`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`storm.topology.submission.notifier.plugin.class: "org.apache.atlas.storm.hook.StormAtlasHook"`}
    </SyntaxHighlighter>
    <p>{`Also set a 'cluster name' that would be used as a namespace for objects registered in Atlas.
This name would be used for namespacing the Storm topology, spouts and bolts.`}</p>
    <p>{`The other objects like data sets should ideally be identified with the cluster name of
the components that generate them. For e.g. Hive tables and databases should be
identified using the cluster name set in Hive. The Storm Atlas hook will pick this up
if the Hive configuration is available in the Storm topology jar that is submitted on
the client and the cluster name is defined there. This happens similarly for HBase
data sets. In case this configuration is not available, the cluster name set in the Storm
configuration will be used.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
atlas.cluster.name: "cluster_name"
    </SyntaxHighlighter>
    <p>{`In `}<em parentName="p">{`$STORM_HOME/conf/storm_env.ini`}</em>{`, set an environment variable as follows:`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
STORM_JAR_JVM_OPTS:"-Datlas.conf=$ATLAS_HOME/conf/"
    </SyntaxHighlighter>
    <p>{`where ATLAS_HOME is pointing to where ATLAS is installed.`}</p>
    <p>{`You could also set this up programmatically in Storm Config as:`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
    {`Config stormConf = new Config();
        ...
        stormConf.put(Config.STORM_TOPOLOGY_SUBMISSION_NOTIFIER_PLUGIN,
                org.apache.atlas.storm.hook.StormAtlasHook.class.getName());`}
    </SyntaxHighlighter>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;